import type { ReactNode, SVGProps } from "react";

export type ShopFloorIconName =
  | "add-sale"
  | "arrow-left"
  | "bouquets"
  | "box"
  | "camera"
  | "calendar"
  | "check"
  | "chevron-right"
  | "close"
  | "collapse"
  | "document"
  | "delivery"
  | "expand"
  | "flag"
  | "lock"
  | "menu"
  | "notes"
  | "orders"
  | "pen"
  | "print"
  | "plus"
  | "search"
  | "stock"
  | "store"
  | "tasks"
  | "waste";

interface ShopFloorIconProps extends Omit<SVGProps<SVGSVGElement>, "children"> {
  name: ShopFloorIconName;
  size?: number;
}

/** Lightweight, original line icons for the shop-floor interface. */
export function ShopFloorIcon({
  name,
  size = 24,
  className,
  ...props
}: ShopFloorIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {iconPaths[name]}
    </svg>
  );
}

const iconPaths: Record<ShopFloorIconName, ReactNode> = {
  box: (
    <>
      <path d="M4 8.5 12 4.5l8 4v9l-8 4-8-4v-9Z" />
      <path d="M4 8.5l8 4 8-4M12 12.5v9" />
      <path d="M8 6.5l8 4" />
    </>
  ),
  print: (
    <>
      <path d="M7 9V4.5h10V9" />
      <path d="M5 9h14a1.5 1.5 0 0 1 1.5 1.5v5H17.5v-2h-11v2H3.5v-5A1.5 1.5 0 0 1 5 9Z" />
      <path d="M6.5 13.5h11v6h-11v-6Z" />
    </>
  ),
  "add-sale": (
    <>
      <path d="M5.5 8.5h13l-1 11h-11l-1-11Z" />
      <path d="M9 8.5V6.8a3 3 0 0 1 6 0v1.7" />
      <path d="M12 12v4.5M9.75 14.25h4.5" />
    </>
  ),
  "arrow-left": <path d="m14.5 5-7 7 7 7M8 12h11" />,
  bouquets: (
    <>
      <g fill="currentColor" stroke="none">
        <ellipse cx="11.5" cy="3.8" rx="1.85" ry="2.9" />
        <ellipse
          cx="14.65"
          cy="5.65"
          rx="1.85"
          ry="2.9"
          transform="rotate(58 14.65 5.65)"
        />
        <ellipse
          cx="14.65"
          cy="9.25"
          rx="1.85"
          ry="2.9"
          transform="rotate(118 14.65 9.25)"
        />
        <ellipse cx="11.5" cy="11.1" rx="1.85" ry="2.9" />
        <ellipse
          cx="8.35"
          cy="9.25"
          rx="1.85"
          ry="2.9"
          transform="rotate(58 8.35 9.25)"
        />
        <ellipse
          cx="8.35"
          cy="5.65"
          rx="1.85"
          ry="2.9"
          transform="rotate(118 8.35 5.65)"
        />
      </g>
      <circle
        cx="11.5"
        cy="7.45"
        r="2.15"
        fill="var(--color-primary)"
        stroke="currentColor"
        strokeWidth="0.7"
      />
      <g fill="currentColor" stroke="none">
        <circle cx="10.75" cy="6.85" r="0.24" />
        <circle cx="12.25" cy="6.9" r="0.24" />
        <circle cx="11.5" cy="7.65" r="0.24" />
        <circle cx="10.8" cy="8.25" r="0.2" />
        <circle cx="12.15" cy="8.25" r="0.2" />
        <path d="M11.8 17.95C9.15 17.35 7.45 15.25 7.3 12.75c2.7.35 4.35 2.3 4.5 5.2Z" />
        <path d="M12.05 18.45c.55-2.9 2.4-5.15 5.2-5.75-.05 2.75-1.65 5.05-5.2 5.75Z" />
      </g>
      <path d="M11.8 9.25c-.55 3.25-.9 7.15.35 10.05.5 1.15 1.25 2.1 2.25 2.85" />
    </>
  ),
  calendar: (
    <>
      <rect x="4" y="5.5" width="16" height="14" rx="2.5" />
      <path d="M8 3.5v4M16 3.5v4M4 9.5h16" />
      <path d="M8 13h.01M12 13h.01M16 13h.01M8 16.5h.01M12 16.5h.01" />
    </>
  ),
  check: <path d="m5 12.5 4.25 4.25L19 7" />,
  "chevron-right": <path d="m9 5 7 7-7 7" />,
  close: <path d="m6 6 12 12M18 6 6 18" />,
  // G-62: the compact-kiosk hamburger, opening the action menu once the
  // sidebar has stepped aside so the till can have the whole tablet.
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  collapse: (
    <>
      <path d="M9 4v5H4M15 20v-5h5M4 9l5-5M20 15l-5 5" />
    </>
  ),
  document: (
    <>
      <path d="M7 3.5h7l4 4v13H7a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2Z" />
      <path d="M14 3.5v4h4M8.5 12h6M8.5 15.5h6" />
    </>
  ),
  delivery: (
    <>
      <path d="M4 7.5 12 3l8 4.5v9L12 21l-8-4.5v-9Z" />
      <path d="m4 7.5 8 4.5 8-4.5M12 12v9" />
      <path d="M12 5.5v4M9.75 7.75 12 10l2.25-2.25" />
    </>
  ),
  expand: (
    <>
      <path d="M9 4H4v5M15 20h5v-5M4 9l5-5M20 15l-5 5" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="10" width="14" height="10" rx="2.5" />
      <path d="M8.5 10V7.5a3.5 3.5 0 0 1 7 0V10M12 14v2.5" />
    </>
  ),
  notes: (
    <>
      <path d="M6 3.5h12v17H6a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2Z" />
      <path d="M8 8h6M8 11.5h6M8 15h3.5M15.5 16.5l2.5 2.5" />
    </>
  ),
  orders: (
    <>
      <path d="m4.5 8 7.5-4 7.5 4v8L12 20l-7.5-4V8Z" />
      <path d="m4.5 8 7.5 4 7.5-4M12 12v8M8.25 6l7.5 4" />
    </>
  ),
  /* G-65: writing a gift note. The ferrule stroke is what stops it reading as
     a generic "edit" arrow — at 26px in the rail it has to say PEN at a
     glance, to a florist who is not studying it. */
  pen: (
    <>
      <path d="M16.8 3.2a2.7 2.7 0 0 1 3.8 3.8L8.4 19.2l-4.6 1.2 1.2-4.6L16.8 3.2Z" />
      <path d="m14.9 5.1 3.8 3.8" />
    </>
  ),
  /**
   * G-72. A flag on a pole, not a warning triangle: this marks a spot for
   * someone to come back to, which is what a bug report is. A triangle reads as
   * "you have done something wrong" and would make florists hesitate to use it.
   */
  flag: (
    <>
      <path d="M5.5 21V3.8" />
      <path d="M5.5 4.4h11.9l-2.3 4.1 2.3 4.1H5.5" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  camera: (
    <>
      <path d="M4 8.5A1.5 1.5 0 0 1 5.5 7H8l1.3-2.2h5.4L16 7h2.5A1.5 1.5 0 0 1 20 8.5V18a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18z" />
      <circle cx="12" cy="13" r="3.5" />
    </>
  ),
  search: (
    <>
      <circle cx="10.5" cy="10.5" r="6" />
      <path d="m15 15 4.5 4.5" />
    </>
  ),
  stock: (
    <>
      <path d="m5 5.5 4-2 4 2v4l-4 2-4-2v-4ZM11 14.5l4-2 4 2v4l-4 2-4-2v-4ZM3 14.5l4-2 4 2v4l-4 2-4-2v-4Z" />
      <path d="m5 5.5 4 2 4-2M9 7.5v4M11 14.5l4 2 4-2M15 16.5v4M3 14.5l4 2 4-2M7 16.5v4" />
    </>
  ),
  store: (
    <>
      <path d="M4 10v9h16v-9M3.5 9.5 5 4.5h14l1.5 5" />
      <path d="M3.5 9.5a2.2 2.2 0 0 0 4.25 0 2.2 2.2 0 0 0 4.25 0 2.2 2.2 0 0 0 4.25 0 2.2 2.2 0 0 0 4.25 0M9 19v-5h6v5" />
    </>
  ),
  tasks: (
    <>
      <rect x="5" y="4.5" width="14" height="16" rx="2.5" />
      <path d="M9 3.5h6v3H9zM8.5 11l1.25 1.25L12 10M14 11h2M8.5 16l1.25 1.25L12 15M14 16h2" />
    </>
  ),
  waste: (
    <>
      <path d="M5 7h14M9 7V4.5h6V7M7 7l1 13h8l1-13M10 10.5v6M14 10.5v6" />
    </>
  ),
};
